import {useEffect, useRef} from 'react';
import {User} from 'hubbl-shared';
import {Api} from '../api';
import {EmployeeChanges} from '../api/ApiCompany';
import {store} from '../store';
import {useAppDispatch, useAppSelector} from './index';
import {CompanyActions} from '../store/company/CompanyActions';
import {CompanySelector} from '../store/company/CompanySelector';
import {UserSelector} from '../store/user/UserSelector';

interface UseEmployeesSyncResult {
  employees: User[];
  loading: boolean;
  hasMore: boolean;
}

/**
 * Hook that provides employees with automatic fetching and real-time sync.
 *
 * - Fetches employees on mount if not already loaded
 * - Sets up real-time Firestore listener for changes
 * - Reconciles cache with Firestore on cold start (handles offline deletions)
 * - Filters out @hubbl.co emails
 *
 * @returns {employees, loading, hasMore} from Redux store
 */
export const useEmployeesSync = (): UseEmployeesSyncResult => {
  const dispatch = useAppDispatch();
  const userDetails = useAppSelector(UserSelector.getUserDetails);
  const {employees, loading, hasMore} = useAppSelector(
    CompanySelector.getCompanyEmployees,
  );
  const companyId = userDetails?.companyId;
  const isListening = useRef(false);
  const isFirstSnapshot = useRef(true);

  // Fetch employees if not already loaded
  useEffect(() => {
    if (hasMore) {
      dispatch(CompanyActions.fetchAllCompanyEmployees());
    }
  }, [dispatch, hasMore]);

  // Real-time sync listener
  useEffect(() => {
    if (!companyId || isListening.current) {
      return;
    }

    isListening.current = true;
    isFirstSnapshot.current = true;

    // Get cached employees at the moment we start listening (not reactive)
    const cachedEmployeeIds = new Set(
      CompanySelector.getCompanyEmployees(store.getState()).employees.map(
        emp => emp.id,
      ),
    );

    const unsubscribe = Api.company.onEmployeesChange(
      companyId,
      (changes: EmployeeChanges) => {
        // Filter out hubbl admins
        const filterHubblAdmins = (emps: typeof changes.added) =>
          emps.filter(emp => !emp.email.includes('@hubbl.co'));

        // On first snapshot, reconcile cache with current Firestore state
        // This handles the "offline" scenario where deletions happened while disconnected
        if (isFirstSnapshot.current) {
          isFirstSnapshot.current = false;

          // Get all current employee IDs from the snapshot
          // (on first snapshot, all existing docs come as "added")
          const currentEmployeeIds = new Set(
            filterHubblAdmins(changes.added).map(emp => emp.id),
          );

          // Remove any cached employees that no longer exist in Firestore
          cachedEmployeeIds.forEach(cachedId => {
            if (!currentEmployeeIds.has(cachedId)) {
              dispatch(CompanyActions.removeEmployee(cachedId));
            }
          });

          // Add any new employees that weren't in cache
          filterHubblAdmins(changes.added).forEach(employee => {
            if (!cachedEmployeeIds.has(employee.id)) {
              dispatch(CompanyActions.addEmployee(employee));
            }
          });

          // Handle modified employees (updates existing data)
          const modifiedFiltered = filterHubblAdmins(changes.modified);
          modifiedFiltered.forEach(employee => {
            dispatch(CompanyActions.updateEmployee(employee));
          });

          return;
        }

        // Normal real-time updates for subsequent snapshots
        // Handle added employees
        const addedFiltered = filterHubblAdmins(changes.added);
        addedFiltered.forEach(employee => {
          dispatch(CompanyActions.addEmployee(employee));
        });

        // Handle modified employees
        const modifiedFiltered = filterHubblAdmins(changes.modified);
        modifiedFiltered.forEach(employee => {
          dispatch(CompanyActions.updateEmployee(employee));
        });

        // Handle removed employees
        changes.removed.forEach(employeeId => {
          dispatch(CompanyActions.removeEmployee(employeeId));
        });
      },
    );

    return () => {
      isListening.current = false;
      unsubscribe();
    };
  }, [companyId, dispatch]);

  return {employees, loading, hasMore};
};
